import React, { useCallback, useEffect } from 'react';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import type { Account } from '../../../generated-types';
import { useTransferAccountsContext } from '../../../hooks/useAccountState';
import useTranslation from '../../../hooks/useTranslation';
import {
    ANALYTICS_END,
    ANALYTICS_START,
    AnalyticsEvents,
    trackEvent,
    trackPageView,
} from '../../../services/analytics';
import { EikaModal } from '../../EikaModal';
import DownpaymentForm from './DownpaymentForm';
import { DownpaymentActionType } from './form/actions';
import useFormContext from './form/useFormContext';

interface DownpaymentProps {
    open: boolean;
    setOpen: React.Dispatch<boolean>;
    account?: Account;
    loanAccount?: Account;
    isLoading?: boolean;
}

const DownpaymentModal: React.FC<DownpaymentProps> = ({ open, setOpen, account, isLoading, loanAccount }) => {
    const translated = useTranslation();

    const { handleChange, mutation } = useFormContext();
    const { targetFromAccount, targetToAccount } = useTransferAccountsContext();

    const onCloseDownpayment = useCallback(() => {
        mutation.updateOnChange(targetFromAccount?.accountId, targetToAccount?.accountId ?? loanAccount?.accountId);
        handleChange({
            type: DownpaymentActionType.RESET,
        });
        mutation.reset();
        setOpen(false);
    }, [
        mutation,
        loanAccount?.accountId,
        targetFromAccount?.accountId,
        targetToAccount?.accountId,
        handleChange,
        setOpen,
    ]);

    useEffect(() => {
        if (open) {
            trackEvent(AnalyticsEvents.DOWNPAYMENT, ANALYTICS_CATEGORIES.DOWNPAYMENT, ANALYTICS_START);
            trackPageView(`/${ANALYTICS_CATEGORIES.DOWNPAYMENT}`);
        } else {
            trackEvent(AnalyticsEvents.DOWNPAYMENT, ANALYTICS_CATEGORIES.DOWNPAYMENT, ANALYTICS_END);
        }
    }, [open]);

    return (
        <EikaModal
            id="downpayment"
            className="transaction-modal"
            open={open}
            setOpen={setOpen}
            size="wide"
            ariaLabel={translated.loans.extraDownpayment}
            onClose={onCloseDownpayment}
            timeOut={mutation.isSuccess ? 2500 : undefined}
            desktopMinHeight="small"
            contentShouldStretch
        >
            <DownpaymentForm
                account={account}
                onClose={onCloseDownpayment}
                loanAccount={loanAccount}
                isLoading={isLoading}
            />
        </EikaModal>
    );
};

export default DownpaymentModal;
